import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  CircularProgress,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const UserGuidesTable = ({
  guides,
  loading,
  sortBy,
  sortOrder,
  onSort,
  onEdit,
  onDelete,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleSort = (property) => {
    onSort(property);
  };

  if (loading) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: "center", py: 8 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress sx={{ color: "#9048FB" }} />
                  <Typography
                    sx={{ fontFamily: "Outfit", color: "#6b7280" }}
                  >
                    Loading user guides...
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (guides.length === 0) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: "center", py: 8 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid #e5e7eb",
                    }}
                  >
                    <EditIcon sx={{ fontSize: 40, color: "#9ca3af" }} />
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Outfit",
                        fontWeight: 600,
                        color: "#374151",
                        mb: 1,
                      }}
                    >
                      No user guides found
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Outfit",
                        color: "#6b7280",
                        mb: 3,
                        maxWidth: 400,
                      }}
                    >
                      Create your first user guide to get started.
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        boxShadow: "none",
        "& .MuiTable-root": {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#f8fafc",
              "& .MuiTableCell-head": {
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
                fontWeight: 600,
                fontSize: "14px",
                color: "#374151",
                fontFamily: "Outfit",
                py: 2,
              },
            }}
          >
            <TableCell>
              <TableSortLabel
                active={sortBy === "navigation_name"}
                direction={sortBy === "navigation_name" ? sortOrder : "asc"}
                onClick={() => handleSort("navigation_name")}
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  color: "#374151",
                  "&.Mui-active": {
                    color: "#2B3674",
                  },
                  "&:hover": {
                    color: "#2B3674",
                  },
                }}
              >
                Navigation Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "title"}
                direction={sortBy === "title" ? sortOrder : "asc"}
                onClick={() => handleSort("title")}
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  color: "#374151",
                  "&.Mui-active": {
                    color: "#2B3674",
                  },
                  "&:hover": {
                    color: "#2B3674",
                  },
                }}
              >
                Title
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "createdAt"}
                direction={sortBy === "createdAt" ? sortOrder : "asc"}
                onClick={() => handleSort("createdAt")}
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  color: "#374151",
                  "&.Mui-active": {
                    color: "#2B3674",
                  },
                  "&:hover": {
                    color: "#2B3674",
                  },
                }}
              >
                Created At
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "updatedAt"}
                direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                onClick={() => handleSort("updatedAt")}
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  color: "#374151",
                  "&.Mui-active": {
                    color: "#2B3674",
                  },
                  "&:hover": {
                    color: "#2B3674",
                  },
                }}
              >
                Updated At
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {guides.map((guide) => (
            <TableRow
              key={guide._id}
              sx={{
                "&:hover": {
                  backgroundColor: "#f8fafc",
                },
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid #f1f5f9",
                  fontFamily: "Outfit",
                  fontSize: "14px",
                  color: "#374151",
                  py: 2,
                },
              }}
            >
              <TableCell sx={{ fontWeight: 500, color: "#2B3674" }}>
                {guide.navigation_name || "N/A"}
              </TableCell>
              <TableCell sx={{ color: "#6b7280" }}>
                {guide.title || "N/A"}
              </TableCell>
              <TableCell sx={{ color: "#6b7280" }}>
                {formatDate(guide.createdAt)}
              </TableCell>
              <TableCell sx={{ color: "#6b7280" }}>
                {formatDate(guide.updatedAt)}
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(guide._id)}
                      sx={{
                        color: "#9048FB",
                        "&:hover": {
                          backgroundColor: "#f3e8ff",
                          color: "#7c3aed",
                        },
                        borderRadius: "6px",
                        p: 0.5,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(guide._id)}
                      sx={{
                        color: "#ef4444",
                        "&:hover": {
                          backgroundColor: "#fee2e2",
                          color: "#dc2626",
                        },
                        borderRadius: "6px",
                        p: 0.5,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserGuidesTable;

